/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import { CARD_TYPES, findMatchingThresholds, replaceVariables } from 'carbon-addons-iot-react';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import filter from 'lodash/filter';
import findLastIndex from 'lodash/findLastIndex';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import format from 'string-format';
import moment from 'moment';

import {
  DASHBOARD_DATA_TYPES,
  DATAITEM_TAG_ALERT,
  COLUMNTYPE_TIMESTAMP,
  INTERVALS,
  TIMES,
} from '../../Common/constants/constants';
import { setInitialZoomDomain } from '../../Common/utils/cardUtilityFunctions';
import TableCardNoData from '../components/TableCardNoData/TableCardNoData';
import { formatTimestampToLocalTimestamp } from '../../Common/utils/formatterFunctions';

import InstanceDrilldownLink from 'InstanceDrilldownLink';
import { SortDirection } from 'CommonTypes';
import { handleImageCardVariables, isDataItemAlert } from 'dataFormatterFunctions';

/**
 * Update the TimeSeries card with values from the data layer
 * @param {Object} cardTemplate card template array of attribute names to extract from the events ['temperature','timestamp']
 * @param {Array<Object>} metricValues aggregated data array from the data lake API
 * @param {Array<Number>} domain [startDate, endDate] for chart time range
 * @param {Boolean} isExpanded
 * @param {String} locale so the formatting is correct
 * @param {string} grain so the axis formatting is correct
 * @returns {Object} cardTemplate with a new property ('values') containing an array of metric event data
 * and updates the needsRefresh and isLoading indicators
 */
export const mapTimeSeriesContent = (
  cardTemplate,
  metricValues,
  domain,
  isExpanded,
  locale,
  grain
) => {
  // TODO: this is going to cause performance problems
  const uniqueTimestamps = [
    ...new Set(
      metricValues.map(val => val[cardTemplate.content.timeDataSourceId] || val.timestamp)
    ),
  ];

  // determine initial zoomDomain based off current domainRange
  const initialZoomDomain = setInitialZoomDomain(uniqueTimestamps);

  return {
    ...cardTemplate,
    content: {
      includeZeroOnXaxis: false, // excludes the zero by default but allow them to override
      includeZeroOnYaxis: false, // excludes the zero by default but allow them to override in the next line
      ...cardTemplate.content,
      timeDataSourceId: cardTemplate.content.timeDataSourceId || 'timestamp',
      // see https://github.com/carbon-design-system/carbon-charts/issues/740 for bug on rendering single data point
      // only add zoomBar if there is more than 1 unique timestamp AKA 1 set of points on a single y-axis gridline
      // only add zoomBar if isExpanded regardless of card size
      ...(uniqueTimestamps.length > 1 && isExpanded
        ? {
            zoomBar: {
              axes: 'top',
              enabled: true,
              initialZoomDomain,
              // TODO: add slider_view once carbon charts fixes the background theme
              // ...(isExpanded ? { view: 'graph_view' } : { view: 'slider_view' }),
              view: 'graph_view',
            },
          }
        : { zoomBar: {} }),
      // Never show empty white space in the chart
      addSpaceOnEdges: 0,
    },
    domainRange: domain,
    values: metricValues,
    locale,
    ...(grain ? { interval: grain } : {}),
  };
};

/**
 * Update the BarChart card with values from the data layer
 * @param {*} cardTemplate card template array of attribute names to extract from the events ['temperature','timestamp']
 * @param {*} metricValues aggregated data array from the data lake API
 * @param {Array<Number>} domain [startDate, endDate] for chart time range
 * @param {String} locale so the formatting is correct
 * @param {string} grain so the axis formatting is correct
 * @returns {Object} cardTemplate with a new property ('values') containing an array of metric event data
 * and updates the needsRefresh and isLoading indicators
 */
export const mapBarChartContent = (cardTemplate, metricValues, domain, locale, grain) => ({
  ...cardTemplate,
  // only add domain if its a time-based bar chart
  ...(cardTemplate.content.timeDataSourceId ? { domainRange: domain } : {}),
  values: metricValues,
  locale,
  ...(grain ? { interval: grain } : {}),
});

/**
 * Update the Table card with values from the data layer
 * @param {Object} cardTemplate .content array of attribute names to extract from the events ['temperature','timestamp']
 * @param {Array} metricValues aggregated data array from the data lake API
 * @param {boolean} hasInstanceDashboard decides to show or not the link in deviceid column
 * @param {string} entityType the name of the entityType
 * @returns {Object} cardTemplate with a new property ('values') containing an array of metric event data and updates the needsRefresh and isLoading indicators
 */
export const mapTableContent = (
  cardTemplate,
  metricValues,
  hasInstanceDashboard,
  entityType,
  locale
) => {
  // I need to update the column definition to handle rendering the Instance drilldowns if there's a device_id column
  const { allowNavigation } = cardTemplate.content;

  return {
    ...cardTemplate,
    content: {
      ...cardTemplate.content,
      locale,
      columns: cardTemplate.content?.columns.map(column => ({
        ...column,
        // Only add the drilldown if the table is showing the device id or entity_id
        ...((column.dataSourceId?.includes('deviceid') ||
          column.dataSourceId?.includes('entity_id')) &&
        allowNavigation !== false &&
        hasInstanceDashboard
          ? {
              renderDataFunction: props => (
                <InstanceDrilldownLink
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  endDateOffset={cardTemplate.timeGrain}
                  row={{
                    // eslint-disable-next-line react/destructuring-assignment, react/prop-types
                    ...props.row,
                    entityTypeName: cardTemplate?.dataSource?.type || entityType,
                  }}
                />
              ),
            }
          : {}),
        sort: column.type === COLUMNTYPE_TIMESTAMP ? SortDirection.DESC : undefined, // if we are passed a timestamp column sort it descending on default
      })),
      // remap the dataSourceId to 'id' for the component
      expandedRows: cardTemplate.content.expandedRows
        ? cardTemplate.content.expandedRows.map(row => ({
            ...row,
            id: row.dataSourceId,
          }))
        : [],
      emptyMessage: <TableCardNoData type={cardTemplate.content.rowTypeName} />,
    },
    // table values need row ids
    values: metricValues.map((i, idx) => ({
      id: `row-${idx}`,
      values: i,
    })),
  };
};

/** Search thresholds for a matching value */
export const findHotspotThreshold = (thresholds, values) => {
  // if the threshold has a variable we need to replace it with its corresponding value
  const updatedThresholds = thresholds.map(threshold => {
    const { value } = threshold;
    if (typeof value === 'string') {
      let updatedValue;
      const regex = /{[a-zA-Z0-9_-]+}/g;
      const templateValue =
        value &&
        Array.isArray(value.match(regex)) &&
        value.match(regex).map(variable => variable.replace('{', '').replace('}', ''));
      if (Array.isArray(templateValue)) {
        updatedValue = replaceVariables(templateValue, values, value);
      }
      return {
        ...threshold,
        value: updatedValue || value,
      };
    }
    return threshold;
  });
  const matchingThreshold = findMatchingThresholds(updatedThresholds, values);
  return matchingThreshold.length > 0 ? matchingThreshold[0] : null;
};

/** returns a hotspot component based on metric values and config */
export const buildHotspot = (
  hotspotMetricValue,
  baseHotspotConfig,
  x,
  y,
  hasInstanceDashboard,
  entityType
) => {
  const {
    icon = undefined,
    color = '#3d70b2',
    content: { title, description, attributes },
    thresholds,
    type,
    backgroundColor,
    backgroundOpacity,
    bold,
    borderColor,
    borderWidth,
    fontColor,
    fontSize,
    italic,
    underline,
  } = baseHotspotConfig;

  /** Find the threshold to show for the overall icon */
  const hotspotThreshold =
    hotspotMetricValue && Array.isArray(thresholds)
      ? findHotspotThreshold(thresholds, hotspotMetricValue)
      : null;

  const hotspotIconColor = pick(hotspotThreshold || { icon, color }, 'icon', 'color');

  let entity = null;
  let instanceLink = null;
  // need to check hotspot title for variables
  const regex = /{[a-zA-Z0-9_-]+}/g;
  const templateTitle = title && title.match(regex);
  let formattedTemplateStrings = {};
  if (Array.isArray(templateTitle)) {
    templateTitle.forEach(i => {
      const index = i.replace('{', '').replace('}', '');

      // special case to handle deviceid and entity_id
      if (index === 'deviceid' || index === 'entity_id') {
        // Find the entity value for the drilldown link
        entity = hotspotMetricValue.deviceid || hotspotMetricValue.entity_id;
        formattedTemplateStrings = { ...formattedTemplateStrings, [index]: entity };
      } else {
        formattedTemplateStrings = hotspotMetricValue[index]
          ? { ...formattedTemplateStrings, [index]: hotspotMetricValue[index] }
          : { ...formattedTemplateStrings, [index]: '' };
      }
    });
  }

  // drilldown to instance dashboard
  if (
    Array.isArray(templateTitle) &&
    (templateTitle.includes('{deviceid}') || templateTitle.includes('{entity_id}'))
  ) {
    // instance dashboard link
    instanceLink = hasInstanceDashboard ? (
      <InstanceDrilldownLink
        value={entity}
        row={{ entityTypeName: entityType }}
        title={format(title, formattedTemplateStrings)}
      />
    ) : (
      format(title, formattedTemplateStrings)
    );
  }

  // need to check hotspot description for variables and replace them
  const templateDescription =
    description &&
    Array.isArray(description.match(regex)) &&
    description.match(regex).map(variable => variable.replace('{', '').replace('}', ''));
  let updatedDescription;
  if (Array.isArray(templateDescription)) {
    updatedDescription = replaceVariables(templateDescription, hotspotMetricValue, description);
  }

  return {
    x: parseFloat(x),
    y: parseFloat(y),
    type,
    content: {
      title: instanceLink || title,
      description: updatedDescription || description,
      attributes,
      values: hotspotMetricValue,
      hotspotThreshold,
    },
    ...hotspotIconColor,
    backgroundColor,
    backgroundOpacity,
    bold,
    borderColor,
    borderWidth,
    fontColor,
    fontSize,
    italic,
    underline,
  };
};

/**
 * Build the hotspot components supporting dynamic locations, returns an array of location based hotspots for the
 * hotspot configuration.
 * */
export const buildHotspotForEachLocation = (
  metricValues,
  baseHotspotConfig,
  hasInstanceDashboard,
  entityType
) => {
  const { locations } = baseHotspotConfig;
  return locations &&
    locations.length === 1 &&
    locations[0].xDataSourceId &&
    locations[0].yDataSourceId // the hotspots are dynamic
    ? metricValues
        .filter(
          metricValue =>
            !isNil(metricValue[locations[0].xDataSourceId]) &&
            !isNil(metricValue[locations[0].yDataSourceId])
        ) // TODO: remove duplicate hotspots with same x and y
        .map(hotspotMetricValue =>
          buildHotspot(
            hotspotMetricValue,
            baseHotspotConfig,
            hotspotMetricValue[locations[0].xDataSourceId],
            hotspotMetricValue[locations[0].yDataSourceId],
            hasInstanceDashboard,
            entityType
          )
        )
    : // Otherwise the hotspots are static
      baseHotspotConfig.locations
        .map(({ x, y, dataFilter }) => {
          // find all metric values that match all dataFilter values...
          const filteredValues = filter(metricValues, dataFilter);
          const hotspotMetricValue = !isEmpty(filteredValues)
            ? filteredValues.reduce((acc, value) => merge(acc, value))
            : null;
          // return an object with all attributes being displayed in the hotspot content
          // if a filter was specified but no metric values were found, don't show the hotspot
          return hotspotMetricValue || isEmpty(dataFilter)
            ? buildHotspot(
                hotspotMetricValue,
                baseHotspotConfig,
                x,
                y,
                hasInstanceDashboard,
                entityType
              )
            : null;
        })
        .filter(i => i);
};

/**
 * Update the Image card with values from the data layer
 * @param {*} cardTemplate card template array of attribute names to extract from the events ['temperature','timestamp']
 * @param {*} metricValues aggregated data array from the data lake API
 * @param {*} hasInstanceDashboard is this an instance dashboard
 * @param {*} entityType needed to map the InstanceDrilldownLink
 *
 * @returns {*} cardTemplate with a new property ('values') containing an array of metric event data and updates the needsRefresh and isLoading indicators
 */
export const mapImageContent = (cardTemplate, metricValues, hasInstanceDashboard, entityType) => ({
  ...cardTemplate,
  isHotspotDataLoading: false,
  values: {
    hotspots: cardTemplate.content.hotspots
      ? cardTemplate.content.hotspots.reduce(
          (acc, hotspot) =>
            acc.concat(
              buildHotspotForEachLocation(metricValues, hotspot, hasInstanceDashboard, entityType)
            ),
          []
        )
      : [],
  },
});

/**
 *
 * @param {*} metricValues either an object of values, or an array of objects if it's an array of objects, only convert it to timestamp
 * @param {Array<Object/>} dataItems array of objects that contain metadata about the values used for formatting
 * @param {boolean} hasDataFilter if there's a data filter do not reduce it into one object
 */
export const mapDataToCorrectFormat = (metricValues, dataItems, locale, hasDataFilter, timeZone) =>
  Array.isArray(metricValues)
    ? !hasDataFilter // no data filter means we can just flatten the array
      ? metricValues.reduce((acc, data) => {
          // map any timestamp values to the correct format
          const formattedData = Object.fromEntries(
            Object.entries(data).map(([dataItemName, value]) => {
              const matchingDataItem = Array.isArray(dataItems)
                ? dataItems.find(dataItem => dataItem.name === dataItemName)
                : null;
              if (matchingDataItem && matchingDataItem.columnType === COLUMNTYPE_TIMESTAMP) {
                return [dataItemName, formatTimestampToLocalTimestamp(value, locale, timeZone)];
              }
              return [dataItemName, value];
            })
          );
          // eslint-disable-next-line no-param-reassign
          acc = { ...acc, ...formattedData };
          return acc;
        }, {})
      : metricValues
          .map(data => {
            // data filter means we need to preserve the array
            const objectFieldsInEntriesFormat = Object.entries(data).map(
              ([dataItemName, value]) => {
                const matchingDataItem = Array.isArray(dataItems)
                  ? dataItems.find(dataItem => dataItem.name === dataItemName)
                  : null;
                if (matchingDataItem && matchingDataItem.columnType === COLUMNTYPE_TIMESTAMP) {
                  return [dataItemName, formatTimestampToLocalTimestamp(value, locale, timeZone)];
                }
                return [dataItemName, value];
              }
            );
            // each one remaps to a entry
            return Object.fromEntries(objectFieldsInEntriesFormat);
          })
          .sort((a, b) => a.timestamp - b.timestamp) // With datafilter: array should be sorted in descending order to pickup latest value from 0th positon
    : Object.fromEntries(
        Object.entries(metricValues).map(([dataItemName, value]) => {
          const matchingDataItem = Array.isArray(dataItems)
            ? dataItems.find(dataItem => dataItem.name === dataItemName)
            : null;
          if (matchingDataItem && matchingDataItem.columnType === COLUMNTYPE_TIMESTAMP) {
            return [dataItemName, formatTimestampToLocalTimestamp(value, locale, timeZone)];
          }
          return [dataItemName, value];
        })
      );

/**
 * This returns the 'aliased' name that the data item was returned by the dashboardData query
 *
 * @param {*} dataItem
 * @param {*} datasourceAttributes array of data attributes from the datasource
 * @returns {string} returns the aliased name of the dataItem
 */
export const resolveDataItemToDataAliasedName = (dataItem, datasourceAttributes) => {
  const matchingAttribute = datasourceAttributes.find(
    datasourceAttribute => datasourceAttribute.attribute === dataItem.name
  );
  return matchingAttribute ? matchingAttribute.id : dataItem.name;
};

/**
 * Maps fetched data to the card with special rules for each card
 * @param {Object} card (see the template schema inside schemas/dashboard.json)
 * @param {Array<Object>} metricValues: raw data object from the data lake API, is an array.  For value cards its an array
 * of one value.  For time series data it's an array of multiple values
 * @param {Boolean} hasInstanceDashboard
 * @param {Array<String>} cardVariables
 * @param {String} deviceId if present, it means this is an instance dashboard
 * @param {Array<Object>} dataItems array of dataItems used for formatting
 * @param {String} entityType name of the entityType
 * @param {String} locale the locale passed down to map to the correct languages
 * @param {String} grain the grain passed down so the xaxis shows the correct tick format
 */
export const cardDataMapper = (
  card,
  metricValues,
  hasInstanceDashboard,
  cardVariables = [],
  deviceId,
  dataItems = [],
  entityType,
  locale,
  grain,
  timeZone
) => {
  // find the variables for this card and add them on a new card prop called 'cardVariables'
  const variables = {};

  cardVariables.forEach(variable => {
    if (variable === 'deviceid' && deviceId) {
      variables[variable] = deviceId;
    } else if (variable === 'entity_id' && deviceId) {
      // sometimes they use device id
      variables[variable] = deviceId;
    } else if (!isEmpty(metricValues)) {
      const matchingVariableIndex = findLastIndex(
        metricValues,
        metricValue => metricValue[variable]
      );
      if (metricValues[metricValues.length - 1].hasOwnProperty(variable)) {
        variables[variable] = metricValues[metricValues.length - 1][variable];
      } else if (matchingVariableIndex > -1) {
        variables[variable] = metricValues[matchingVariableIndex][variable];
      }
    }
  });

  const timestamps = Array.isArray(metricValues)
    ? metricValues.map(val => val?.[card?.content?.timeDataSourceId] || val?.timestamp)
    : [];

  const updatedCard = !isEmpty(variables) ? { ...card, cardVariables: variables } : card;

  switch (card.type) {
    case CARD_TYPES.VALUE: {
      // Value cards are expecting one object with everything in it if no dataFilter is set
      return {
        ...omit(updatedCard, 'error'),
        values:
          card.dataSource?.type !== DASHBOARD_DATA_TYPES.ALERT
            ? mapDataToCorrectFormat(
                metricValues,
                dataItems.map(dataItem => ({
                  ...dataItem,
                  name: resolveDataItemToDataAliasedName(
                    dataItem,
                    updatedCard?.dataSource?.attributes
                  ),
                })),
                locale,
                Boolean(updatedCard.content.attributes.find(attribute => attribute.dataFilter)),
                timeZone
              )
            : metricValues,
      };
    }

    case CARD_TYPES.TIMESERIES: {
      // TODO: this can cause performance problems
      const domain = [Math.min(...timestamps), Math.max(...timestamps)];
      return mapTimeSeriesContent(
        omit(updatedCard, 'error'),
        metricValues,
        domain,
        card?.isExpanded,
        locale,
        grain
      );
    }
    case CARD_TYPES.BAR: {
      // TODO: this can cause performance problems
      const domain = [Math.min(...timestamps), Math.max(...timestamps)];
      return mapBarChartContent(omit(updatedCard, 'error'), metricValues, domain, locale, grain);
    }

    case CARD_TYPES.TABLE: {
      const tableCard = omit(updatedCard, 'error');
      return mapTableContent(tableCard, metricValues, hasInstanceDashboard, entityType, locale);
    }

    case CARD_TYPES.IMAGE: {
      const updatedMetricValues = [];
      metricValues.forEach(metricValue => {
        const updatedValue = {};
        Object.entries(metricValue).forEach(([key, value]) => {
          const matchingDataItem = Array.isArray(dataItems)
            ? dataItems.find(dataItem => dataItem.name === key)
            : null;
          if (matchingDataItem && matchingDataItem.columnType === COLUMNTYPE_TIMESTAMP) {
            updatedValue[key] = formatTimestampToLocalTimestamp(value, locale, timeZone);
          } else {
            updatedValue[key] = value;
          }
        });
        updatedMetricValues.push(updatedValue);
      });

      // don't handle cardVariables in summary dashboard yet, as we need to do some mapping first TODO: is this true?
      return !deviceId
        ? mapImageContent(
            omit(updatedCard, 'error'),
            updatedMetricValues,
            hasInstanceDashboard,
            entityType
          )
        : mapImageContent(
            omit(handleImageCardVariables(updatedCard), 'error'),
            updatedMetricValues,
            hasInstanceDashboard,
            entityType
          );
    }

    default:
      return { ...updatedCard, values: metricValues };
  }
};
/**
 * Return the object map of alertName and corresponding alert input data item name.
 * ex:
 * lets have two data Item.
 * 1) load (raw metric)
 * 2) Load_Alert (Alert created on top of raw metric load)
 * @param {*} dataItems  list of dataItems of type ALERT
 * @returns {object} ex: response { "Load_Alert":"load" }
 */
export const getAlertInputMap = dataItems => {
  const alertInputMap = {};
  dataItems.forEach(dataItem => {
    const { name, kpiFunctionDto } = dataItem;
    // TODO: Same KPI input with different eventName case need to be handled.
    const { input } = kpiFunctionDto;
    const {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      input_item, // used in V1 alert catalog function
      value, // used in V2 alert catalog function
    } = input;
    alertInputMap[name] = input_item || value;
  });
  return alertInputMap;
};

/**
 * Return the object map of alertName and corresponding dataSourceIds
 * @param {*} card card JSON
 * @param {*} alertInputMap object map generated from getAlertInputMap function
 * @returns {object} ex: response { "Load_Alert":["dataSourceID1",""dataSourceID2""] }
 */
/*
const getAlertMatchingDataSourceIds = (card, alertInputMap) => {
  const alertMatchingDataSourceIdsMap = {};

  if (card?.dataSource?.attributes) {
    const { dataSource } = card;
    const { attributes } = dataSource;

    Object.entries(alertInputMap).forEach(([alertName, alertInputSource]) => {
      alertMatchingDataSourceIdsMap[alertName] = attributes
        .filter(
          attribute =>
            attribute.attribute === alertInputSource && !attribute.hasOwnProperty('aggregator')
        )
        .map(attribute => attribute.id);
    });
  }

  return alertMatchingDataSourceIdsMap;
};
*/

/**
 *
 * Currently we only support mapping alert data into timeseries charts.  This builds an alert range for each additionalData point passed in
 * TODO: Will update the startTimestamp and endTimestamp once this information is provided by the new alert object
 *
 * @param {Object} card, full card content
 * @param {Array<Object>} additionalData, set of additional data to map
 */
export const mapAdditionalData = (card, additionalData, dataItems) => {
  const alertDataItems = dataItems?.filter(
    dataItem => isDataItemAlert(dataItem) || dataItem.tags.includes(DATAITEM_TAG_ALERT)
  );

  // If there are any active alerts then only we can plot the alert value. We use the alert configuration to plot the red dots.
  if (card.type === CARD_TYPES.TIMESERIES && !isEmpty(alertDataItems) && !isEmpty(additionalData)) {
    const alertInputMap = getAlertInputMap(alertDataItems);
    // const alertMatchingDataSourceIdsMap = getAlertMatchingDataSourceIds(card, alertInputMap);
    const alertRanges =
      additionalData
        .filter(alert => !isNil(alertInputMap[alert.name])) // If the alert data items are not present anymore but the alerts are availabe in backend then dont show
        .map(alert => {
          const { timestamp, name } = alert;
          const convertedTimestamp = new Date(timestamp).valueOf();

          return {
            color: '#FF0000',
            startTimestamp: convertedTimestamp,
            endTimestamp: convertedTimestamp,
            details: `${name}`,
            // inputSource: {
            //   name: alertInputMap[name],
            //   // eventName: "" // TODO: Same KPI input with different eventName case need to be handled.
            //   dataSourceIds: alertMatchingDataSourceIdsMap[name],
            // },
          };
        }) || [];

    return {
      ...card,
      content: {
        ...card.content,
        alertRanges,
      },
    };
  }

  return card;
};

/**
 *
 * Determines interval to be used for line chart
 *
 * @param {Object} timeValues, list of object that contains timestamp of value
 */
export const getTimeInterval = (timeValues = []) => {
  if (!isEmpty(timeValues)) {
    // Getting start date and end date from card values - assumption card values are sorted
    const startDate = moment(timeValues[0]?.timestamp);
    const endDate = moment(timeValues[timeValues.length - 1]?.timestamp);

    // Get difference in highest and smallest dates from card values
    const diffInMinutes = Math.abs(startDate.diff(endDate, 'minutes'));
    const diffInDays = diffInMinutes / TIMES.ONE_DAY_IN_MINS;

    if (diffInMinutes <= TIMES.HALF_A_DAY_IN_MINS) {
      return INTERVALS.MINUTE;
    }
    if (diffInMinutes <= TIMES.ONE_DAY_IN_MINS) {
      return INTERVALS.HOUR;
    }
    if (diffInDays <= TIMES.ONE_MONTH) {
      return INTERVALS.DAY;
    }
    if (diffInDays <= TIMES.ONE_QTR) {
      return INTERVALS.WEEK;
    }
    if (diffInDays <= TIMES.HALF_A_YEAR) {
      return INTERVALS.QUARTER;
    }
    if (diffInDays <= TIMES.ONE_YEAR) {
      return INTERVALS.MONTH;
    }
    return INTERVALS.YEAR;
  }

  return INTERVALS.DAY;
};
